/**
 * The seven things the plan implies and the server does not publish.
 *
 * Task 45 found four of these, Task 53 seven, Task 48 seven more. The answer each
 * time was the same and it is the answer here: **named on screen, with the endpoint
 * that would close it and its owner, and never mocked.**
 *
 * Task 48's wording is followed deliberately — *"specified, not mounted"* rather than
 * "no events" — because an empty result tells the reader there was nothing to see.
 * None of the seven below is even specified: they are absences in `openapi.yaml`
 * itself, which `availability.test.ts` asserts by searching the document, in both
 * directions, so this panel goes red the day one of them lands rather than going on
 * apologising for it. (Task 53 printed a sentence about the incident timeline that
 * was true when written and false a week later; G19 had to delete it. This is that
 * lesson, mechanised.)
 *
 * **No controls, and specifically no disabled ones.** Nothing here can be pressed
 * into existence; a greyed button would assert "this could do something, but not for
 * you" when the truth is that nothing could.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { ABSENT_AI_SURFACES } from '../../ai/availability';
import { StatusNote } from '../../states/StatusNote';

const NOTE: Readonly<Record<string, MessageKey>> = {
  budget: 'm4.absent.budget',
  insightById: 'm4.absent.insightById',
  alertById: 'm4.absent.alertById',
  dailyById: 'm4.absent.dailyById',
  jobStatus: 'm4.absent.jobStatus',
  conversation: 'm4.absent.conversation',
  streaming: 'm4.absent.streaming',
};

export function AbsentSurfaces(): ReactNode {
  const { t } = useI18n();
  return (
    <section className="wp-section" aria-labelledby="m4-absent-heading" data-testid="m4-absent">
      {/* Seven bullets and a heading used to sit open on the page, under a screen
          that works — a wall of apology between the reader and the analysis. The
          facts are unchanged and one click away (finish review, fix 4). */}
      <details className="wp-disclosure">
        <summary className="wp-disclosure__summary" id="m4-absent-heading">
          {t('m4.absent.title')}
        </summary>
        <p className="wp-small">{t('m4.absent.body')}</p>
        {/* One sentence per gap, in the reader's own language. The endpoint that
          would close each one is engineering's business, not the reader's: it
          stays in `ai/availability.ts`, where a test still checks it against the
          published contract so this list cannot go on apologising for something
          somebody has finished. */}
        <ul className="wp-list" data-testid="m4-absent-list">
          {ABSENT_AI_SURFACES.map((surface) => (
            <li key={surface.id} data-testid={`m4-absent-${surface.id}`}>
              {t(NOTE[surface.id] ?? 'm4.absent.body')}
            </li>
          ))}
        </ul>
        <StatusNote severity="info" title={t('m4.absent.title')} testId="m4-absent-note">
          <p>{t('m4.refusal.budget.noEndpoint')}</p>
        </StatusNote>
      </details>
    </section>
  );
}
